import ExcelJS from "exceljs";
import { Alert, Button, Card, Space, Typography, message } from "antd";
import { useState } from "react";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { AiIntelligentAssistantIndexFileName, UploadExcelFileType } from "@/config";
import RequiredMark from "@/views/components/RequiredMark";

// 对齐 Vue batchUpload 的 Excel 文件大小边界，2MB 以内允许继续解析。
const MAX_HOT_WORD_FILE_SIZE = 2 * 1024 * 1024;

export interface AiHotWordImportRow {
    hotWord: string;
    weight: number | string;
    errors: string[];
}

export interface AiHotWordBatchResponse {
    flag?: number;
    count?: number;
    errors?: Array<{ index: number; errorMsg: string }>;
    message?: string;
}

export interface AiHotWordUploadPageProps {
    initialRows?: AiHotWordImportRow[];
    validateBatch?: (rows: AiHotWordImportRow[]) => Promise<AiHotWordBatchResponse>;
    submitBatch?: (rows: AiHotWordImportRow[]) => Promise<AiHotWordBatchResponse>;
}

/** 校验 Vue Excel 模板的固定表头。 */
export function validateHotWordTemplate(headers: string[]): boolean {
    return headers[0]?.trim() === "AI热词" && headers[1]?.trim() === "权重";
}

/** 将 Excel 行转换为 Vue 批量导入行，并复用热词边界校验。 */
export function buildHotWordImportRows(rows: string[][]): AiHotWordImportRow[] {
    return rows.map(values => {
        const hotWord = values[0]?.trim() || "";
        const rawWeight = values[1]?.trim() || "";
        const errors: string[] = [];
        const weight = rawWeight === "" ? "" : Number(rawWeight);
        if (!hotWord) errors.push("热词为空");
        else if (!/^[\u4e00-\u9fa50-9]*$/.test(hotWord)) errors.push("热词只能输入中文和阿拉伯数字");
        if (rawWeight === "") errors.push("权重值为空");
        else if (!Number.isInteger(weight) || Number(weight) < -6 || Number(weight) > 5) {
            errors.push("权重必须为 -6 至 5 的整数");
        }
        return { hotWord, weight, errors };
    });
}

/** 解析 xlsx 第一张表，保持 Vue 模板的表头和数据行契约。 */
export async function parseHotWordWorkbook(file: Blob): Promise<AiHotWordImportRow[]> {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const worksheet = workbook.worksheets[0];
    const rows: string[][] = [];
    worksheet.eachRow(row => {
        rows.push((row.values as unknown[]).slice(1).map(value => String(value ?? "")));
    });
    if (!validateHotWordTemplate(rows[0] || []))
        throw new Error("当前的Excel与模板不一致，请检查Excel或重新下载最新模板编辑!");
    return buildHotWordImportRows(rows.slice(1).filter(values => values.some(value => value.trim() !== "")));
}

function batchPayload(rows: AiHotWordImportRow[]) {
    return { hotWordForms: rows.map(({ hotWord, weight }) => ({ hotWord, weight })) };
}

/** 调用 Vue step=1 接口做导入预检。 */
export async function validateAiHotWordBatch(rows: AiHotWordImportRow[]): Promise<AiHotWordBatchResponse> {
    const response = await request.service.post<AiHotWordBatchResponse>("/hot-word/batchImport/1", batchPayload(rows));
    return response.data;
}

/** 调用 Vue step=2 接口提交已校验的热词。 */
export async function submitAiHotWordBatch(rows: AiHotWordImportRow[]): Promise<AiHotWordBatchResponse> {
    const response = await request.service.post<AiHotWordBatchResponse>("/hot-word/batchImport/2", batchPayload(rows));
    return response.data;
}

/** 生成错误数据 Excel，保留 Vue 的三列表头和错误信息。 */
export async function createHotWordErrorWorkbook(rows: AiHotWordImportRow[]): Promise<Blob> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("AI热词");
    worksheet.addRow(["AI热词", "权重", "数据校验状态"]);
    rows.filter(row => row.errors.length).forEach(row =>
        worksheet.addRow([row.hotWord, row.weight, row.errors.join("，")])
    );
    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer]);
}

/** AI 热词批量导入页，保留 Vue 的解析、预检、错误展示和 step=2 提交。 */
export function AiHotWordUploadPage({
    initialRows = [],
    validateBatch = validateAiHotWordBatch,
    submitBatch = submitAiHotWordBatch
}: AiHotWordUploadPageProps) {
    const [rows, setRows] = useState<AiHotWordImportRow[]>(initialRows);
    const [errorRows, setErrorRows] = useState<AiHotWordImportRow[]>(initialRows.filter(row => row.errors.length));
    const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);

    const handleFileChange = async (file?: File) => {
        if (!file) return;
        if (!UploadExcelFileType.includes(file.type)) {
            message.error("文件须为Excel格式");
            return;
        }
        // 文件超限时在解析前结束流程，避免读取文件内容或触发后续接口。
        if (file.size > MAX_HOT_WORD_FILE_SIZE) {
            message.error("文件不能超过2MB");
            return;
        }
        try {
            const parsed = await parseHotWordWorkbook(file);
            setRows(parsed);
            setErrorRows(parsed.filter(row => row.errors.length));
            if (!parsed.length) message.warning("Excel中无数据，请添加后再上传!");
        } catch (error) {
            message.error(error instanceof Error ? error.message : "Excel解析失败");
        }
    };

    const handleValidate = async () => {
        const localErrors = rows.filter(row => row.errors.length);
        if (!rows.length) {
            message.warning("请先上传Excel文件");
            return;
        }
        const result = await validateBatch(rows);
        const serviceErrors = result.errors || [];
        const nextRows = rows.map((row, index) => ({
            ...row,
            errors: [
                ...row.errors,
                ...serviceErrors.filter(error => error.index === index + 1).map(error => error.errorMsg)
            ]
        }));
        setRows(nextRows);
        setErrorRows(nextRows.filter(row => row.errors.length));
        if (localErrors.length || serviceErrors.length) message.error("数据有误，请修正后再次导入");
        else message.success("数据校验成功");
    };

    const handleSubmit = async () => {
        if (!rows.length) {
            message.warning("请先上传Excel文件");
            return;
        }
        if (rows.some(row => row.errors.length)) {
            message.error("数据有误，请修正后再次导入");
            return;
        }
        const result = await submitBatch(rows);
        if (result.flag) {
            setFeedbackMessage(null);
            message.success(`已新增${result.count || 0}条数据`);
        } else {
            // 错误直接落在页面内，避免全局 toast 在复杂页面中被其他消息遮挡。
            setFeedbackMessage(result.message || "热词导入失败");
        }
    };

    const exportErrors = async () => {
        const blob = await createHotWordErrorWorkbook(errorRows);
        const link = document.createElement("a");
        link.download = "AI热词错误数据.xlsx";
        link.href = URL.createObjectURL(blob);
        link.click();
        URL.revokeObjectURL(link.href);
    };

    // 用 zhihao-ui-react 列契约承接旧 Vue 批量导入预览的序号、字段和校验状态。
    const columns: TableColumnProps<AiHotWordImportRow>[] = [
        { key: "index", label: "序号", minWidth: 120, prop: "hotWord", render: ({ rowIndex }) => rowIndex + 1 },
        { key: "hotWord", label: "AI热词", minWidth: 350, prop: "hotWord", render: ({ row }) => row.hotWord },
        { key: "weight", label: "权重", minWidth: 120, prop: "weight", render: ({ row }) => row.weight },
        {
            key: "errors",
            label: "数据校验状态",
            minWidth: 204,
            prop: "errors",
            render: ({ row }) => (row.errors.length ? row.errors.join("，") : "数据校验成功")
        }
    ];

    return (
        <section aria-label="ai-hot-word-upload-page" className="flex h-full min-h-full flex-col">
            <Card>
                {feedbackMessage ? (
                    <Alert className="mb-4" message={feedbackMessage} role="alert" type="error" />
                ) : null}
                <Space orientation="vertical" className="w-full">
                    <Typography.Title level={4}>AI热词批量导入</Typography.Title>
                    <Space>
                        <a download href={AiIntelligentAssistantIndexFileName}>
                            下载模版
                        </a>
                        <span>文件须为Excel格式，2MB以内</span>
                    </Space>
                    <label htmlFor="ai-hot-word-excel">
                        <RequiredMark />
                        选择Excel文件
                    </label>
                    <input
                        id="ai-hot-word-excel"
                        aria-label="AI热词Excel文件"
                        accept={UploadExcelFileType.join(",")}
                        type="file"
                        onChange={event => void handleFileChange(event.target.files?.[0])}
                    />
                    <ZhTable<AiHotWordImportRow>
                        data={rows}
                        columns={columns}
                        pagination={false}
                        // zhihao-ui-react 的 rowKey 只接收行对象，预览数据由当前数组引用生成稳定键。
                        rowKey={row => String(rows.indexOf(row))}
                    />
                    <Space>
                        <Button aria-label="校验导入数据" onClick={() => void handleValidate()}>
                            校验导入数据
                        </Button>
                        <Button aria-label="导出错误数据" onClick={() => void exportErrors()}>
                            批量导出
                        </Button>
                        <Button aria-label="保存导入" type="primary" onClick={() => void handleSubmit()}>
                            保存
                        </Button>
                    </Space>
                </Space>
            </Card>
        </section>
    );
}

export default AiHotWordUploadPage;
